import { isEmpty } from 'lodash-es';
import { mapActions, mapState } from 'vuex';
import qs from 'query-string';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';
import userPositionMixin from '@phoenix/mixins/userPositionMixin';

export default {
  mixins: [userPositionMixin],

  props: {
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    action: {
      type: String,
      default: '',
    },
    forceNoRedirect: {
      type: Boolean,
      default: false,
    },
    shouldSetStateFromQueryParams: {
      type: Boolean,
      default: true,
    },
    shouldUpdateQueryParams: {
      type: Boolean,
      default: true,
    },
    shouldResetResultsIfEmpty: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      inputKeywordId: 'store-locator-search',
    };
  },

  computed: {
    ...mapState('storeSearch', [
      'isResultsComponentPresent',
      'keyword',
      'position',
      'placeId',
      'placeSuggestions',
      'fetchedPlace',
    ]),

    isSuggestionActivated() {
      return getFeatureFlag(FEATURE_FLAG.PLACES_AUTOCOMPLETE_API);
    },

    inputValue() {
      return this.fetchedPlace?.formatted_address ?? this.keyword;
    },
  },

  watch: {
    keyword() {
      this.updateUrlWithoutReloading();
    },

    position() {
      this.updateUrlWithoutReloading();
    },

    placeId() {
      this.updateUrlWithoutReloading();
    },
  },

  created() {
    if (this.shouldSetStateFromQueryParams) {
      this.setStateFromQueryParams();
      window.addEventListener('popstate', this.setStateFromQueryParams);
    }
  },

  methods: {
    ...mapActions('storeSearch', [
      'setKeyword',
      'setPosition',
      'setPlace',
      'fetchSuggestions',
      'resetPlaceId',
      'resetResults',
    ]),

    setStateFromQueryParams() {
      const queryString = qs.parse(window.location.search, {
        arrayFormat: 'bracket',
      });

      if (queryString.keyword && queryString.keyword !== this.keyword) {
        return this.setKeyword(queryString.keyword);
      }

      if (
        queryString.lat &&
        queryString.lng &&
        (isEmpty(this.position) || (queryString.lng !== this.position.lng && queryString.lat !== this.position.lat))
      ) {
        return this.setPosition({ lat: queryString.lat, lng: queryString.lng });
      }

      if (queryString.place_id && queryString.place_id !== this.placeId) {
        return this.setPlace(queryString.place_id);
      }
    },

    useMyPosition() {
      if (navigator.geolocation) {
        this.setPositionBtnLoader(true);
        this.resetPlaceId();
        navigator.geolocation.getCurrentPosition(this.onCurrentPositionSuccess, this.onCurrentPositionError);
      } else {
        window.Phoenix.toaster.notify({
          type: 'danger',
          text: this.$t('StoreSearch.error.NOT_SUPPORTED'),
        });
      }
    },

    onCurrentPositionSuccess(position) {
      position = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      if (this.forceNoRedirect || this.isResultsComponentPresent) {
        this.setPosition(position);
        this.setPositionBtnLoader(false);
      } else {
        this.redirectToAction({ position });
      }
    },

    redirectToAction(params) {
      window.location.href = `${this.action}?${this.getQueryFromParams(params)}`;
    },

    updateUrlWithoutReloading() {
      if (!this.shouldUpdateQueryParams) {
        return;
      }

      const newUrl = `${window.location.protocol}//${window.location.host}${
        window.location.pathname
      }?${this.getQueryFromParams({
        position: this.position,
        keyword: this.keyword,
        placeId: this.placeId,
      })}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    },

    getQueryFromParams(params) {
      let query = '';

      if (!isEmpty(params.keyword)) {
        query = `keyword=${params.keyword}`;
      }

      // When using a place id, we also have a position to get the results so here we want to make sure
      // to update the query only if we have just the position
      if (!isEmpty(params.position) && isEmpty(params.placeId)) {
        query = `lat=${params.position.lat}&lng=${params.position.lng}`;
      }

      if (!isEmpty(params.placeId)) {
        query = `place_id=${params.placeId}`;
      }

      return query;
    },

    async onKeywordInput(input) {
      if (!this.isSuggestionActivated) {
        return;
      }

      await this.fetchSuggestions(input.query);
    },

    searchByKeyword(keyword) {
      if (this.forceNoRedirect || this.isResultsComponentPresent) {
        this.setKeyword(keyword);
        this.updateUrlWithoutReloading();
      } else {
        this.redirectToAction({ keyword });
      }
    },

    searchByPlace(placeId) {
      if (this.forceNoRedirect || this.isResultsComponentPresent) {
        this.setPlace(placeId);
      } else {
        this.redirectToAction({ placeId });
      }
    },

    onSubmit(data) {
      if (data) {
        if (data.query !== undefined) {
          this.searchByKeyword(data.query);
        } else {
          this.searchByPlace(data.key);
        }

        let term = '';
        if (data.query) {
          term = data.query;
        } else if (data.key) {
          term = this.placeSuggestions.find((place) => place.key === data.key)?.suggestion || '';
        }
        window.tracking.pushEvent(window.tracking.getStoreSearchTracking(term));
      } else if (this.shouldResetResultsIfEmpty) {
        this.resetResults();
      }
    },
  },
};
